"use client";

import React, { useCallback } from "react";
import { Button } from "@mantine/core";

// Types
import type { DetailActionProps, BaseEntity } from "../types/detailPage";

// ============================================================================
// COMPANY VIEW ACTION COMPONENT
// ============================================================================

interface CompanyViewActionProps<T extends BaseEntity> extends DetailActionProps<T> {
  /** Company ID field */
  companyField?: string;

  /** Company URL template */
  companyUrlTemplate?: string;
}

/**
 * Reusable Company View Action Component
 * Can be used for any entity that has a company relationship
 */
export function CompanyViewAction<T extends BaseEntity>({
  action,
  item,
  context,
  loading = false,
  companyField = "company_id",
  companyUrlTemplate = "/companies/{companyId}",
}: CompanyViewActionProps<T>) {
  // Handle company view
  const handleViewCompany = useCallback(() => {
    const companyId = (item as any)[companyField];
    if (!companyId) return;

    const companyUrl = companyUrlTemplate.replace("{companyId}", companyId);
    context.onNavigate(companyUrl);
  }, [item, companyField, companyUrlTemplate, context]);

  const companyId = (item as any)[companyField];
  const companyName = (item as any)["company_name"];

  if (!companyId) return null;

  return (
    <Button variant="outline" size="sm" onClick={handleViewCompany} disabled={loading}>
      View {companyName || "Company"}
    </Button>
  );
}

// Export with generic type
export { CompanyViewAction as CompanyViewActionComponent };
